import React from 'react';
import { Meta } from '../../../../styled components/Typography';
import UICard from '../../../../styled components/UICard';
import ListViewTableLoadingRows from '../ListViewTableLoadingRows';
import ListViewTableRow from '../ListViewTableRow/ListViewTableRow';
import { BookingRows, TableHeaderCell, TableScrollBox } from './ListViewTable.style';
import { RESOURCE_NAME_COLUMN_KEY, tableRows } from '../../../../libs/listViewPresentation';
import type {
  ListViewBooking,
  ListViewColumn,
  ListViewDiff,
  ListViewSetCollapse,
  TimeFormatSetting
} from '../../../types';

/* The column the nesting of a set and its members is shown in: the resource
   name, which is what they are told apart by. A view that does not show that
   column falls back to its first one, so the nesting is visible either way. */
function nestingColumnKey(columns: ListViewColumn[]): string {
  const nameColumn = columns.find((column) => column.key === RESOURCE_NAME_COLUMN_KEY);
  return (nameColumn || columns[0]).key;
}

/* The column the "happening now" dot is put in front of: the start time,
   which is what says why the booking is running. A view that does not show
   that column marks the row on its nesting column instead, so the dot has
   somewhere to sit either way. */
function happeningNowColumnKey(columns: ListViewColumn[]): string {
  const startColumn = columns.find((column) => column.key === 'start');
  return startColumn ? startColumn.key : nestingColumnKey(columns);
}

/* A floor under the width of a column, in characters of its own values: a
   column is otherwise only as wide as its widest cell asks for, so one whose
   values are commonly a little wider than that wraps on nearly every row. A
   column that cannot have its minimum widens the table past its box, which
   scrolls sideways to it - see TableScrollBox. */
const minimumColumnWidthInCharacters: Record<string, number> = {
  /* The resource name, which is what the row is read by. Its cell asks the
     table for no width of its own (see NestingCell), so without a floor it
     would be left with whatever the other columns do not need - twenty
     characters is the room a name is worth before the ellipsis takes over */
  resourceName: 20,
  /* The rate and the unit it is per ("100.00 /Day", "1.000,00 /Hour"): twelve
     characters covers the shapes it commonly takes, and a longer one still
     wraps rather than widening the table */
  unitPrice: 12
};

interface ListViewTableProps {
  bookings: ListViewBooking[];
  columns: ListViewColumn[];
  /* The bookings are still being fetched: the columns are known, so the table
     is already laid out and only its rows are skeletons */
  isLoading: boolean;
  /* Whether the member bookings of each set are listed as indented rows of
     their own, under the row of their set */
  setCollapse: ListViewSetCollapse;
  statusByID: ListViewDiff['statusByID'];
  now: string;
  dateFormatSetting: string;
  timeFormatSetting: TimeFormatSetting;
}

/* The table view: all bookings chronologically, one row per booking */
const ListViewTable = ({
  bookings,
  columns,
  isLoading,
  setCollapse,
  statusByID,
  now,
  dateFormatSetting,
  timeFormatSetting
}: ListViewTableProps) => (
  /* The live region is this wrapper rather than anything inside the table: a
     role on the table's own elements would cost them their table semantics */
  <div
    role={isLoading ? 'status' : undefined}
    aria-label={isLoading ? i18n('Loading…') : undefined}
  >
    <UICard $padding={4} data-test-list-view-table>
      <TableScrollBox>
        <table tw="w-full border-collapse">
          <thead>
            <tr>
              {columns.map((column) => (
                <TableHeaderCell
                  key={column.key}
                  $minWidthInCharacters={minimumColumnWidthInCharacters[column.key]}
                  $indentedForHappeningNowDot={
                    column.key === happeningNowColumnKey(columns)
                  }
                >
                  <Meta>{column.label || column.key}</Meta>
                </TableHeaderCell>
              ))}
            </tr>
          </thead>
          <BookingRows>
            {isLoading && (
              <ListViewTableLoadingRows
                columns={columns}
                happeningNowColumnKey={happeningNowColumnKey(columns)}
              />
            )}
            {tableRows(bookings, setCollapse.isSetExpanded).map((row) => (
              <ListViewTableRow
                key={row.id}
                row={row}
                columns={columns}
                nestingColumnKey={nestingColumnKey(columns)}
                happeningNowColumnKey={happeningNowColumnKey(columns)}
                setCollapse={setCollapse}
                statusByID={statusByID}
                now={now}
                dateFormatSetting={dateFormatSetting}
                timeFormatSetting={timeFormatSetting}
              />
            ))}
          </BookingRows>
        </table>
      </TableScrollBox>
    </UICard>
  </div>
);

export default ListViewTable;
